import {
  BoldItalicUnderlineToggles,
  CreateLink,
  MDXEditor,
  UndoRedo,
  headingsPlugin,
  linkPlugin,
  linkDialogPlugin,
  listsPlugin,
  toolbarPlugin,
} from '@mdxeditor/editor';
import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Clock3, FileText, History, Paperclip, RotateCcw, Search} from 'lucide-react';
import {useEffect, useId, useMemo, useRef, useState} from 'react';
import {useSearchParams} from '../router';
import {ApiError, apiRequest} from '../api';
import {SecondaryButton, Surface} from '../design';
import {DocumentArtifact, PageResult, humanizeStage} from '../types';
import {formatDateTime, formatRelativeDate} from './PagePrimitives';

type NoteRevision = {
  id: string;
  sequence: number;
  markdown: string;
  reason: string;
  createdAt: string;
  restoredFromRevisionId?: string;
};

type NoteRecord = {
  id: string;
  title: string;
  markdown: string;
  source: 'user' | 'ai' | 'email_import';
  sourceRefs: string[];
  createdAt: string;
  updatedAt: string;
  version: number;
  revisionCount: number;
  latestRevisionId?: string;
  linkedEntityType?: string;
  linkedEntityId?: string;
};

type NoteRevisionPage = {
  items: NoteRevision[];
  total: number;
  nextBeforeSequence?: number;
};

type ChangedLine = {
  number: number;
  before: string | null;
  after: string | null;
};

type SaveState = {
  phase: 'saved' | 'dirty' | 'saving' | 'queued' | 'error';
  error?: Error;
  conflict?: boolean;
};

export type OpportunityNotesRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

const lineChanges = (before: string, after: string): ChangedLine[] => {
  const earlier = before.split('\n');
  const current = after.split('\n');
  const result: ChangedLine[] = [];
  const length = Math.max(earlier.length, current.length);
  for (let index = 0; index < length && result.length < 120; index += 1) {
    if (earlier[index] === current[index]) continue;
    result.push({
      number: index + 1,
      before: earlier[index] ?? null,
      after: current[index] ?? null,
    });
  }
  return result;
};

export const OpportunityNotes = ({
  opportunityId,
  request = apiRequest,
}: {
  opportunityId: string;
  request?: OpportunityNotesRequest;
}) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [activeNoteId, setActiveNoteId] = useState(() => searchParams.get('note') ?? '');
  const [noteValue, setNoteValue] = useState('');
  const [editorGeneration, setEditorGeneration] = useState(0);
  const [selectedRevisionId, setSelectedRevisionId] = useState('');
  const [saveState, setSaveState] = useState<SaveState>({phase: 'saved'});
  const [lastSavedAt, setLastSavedAt] = useState('');
  const [editorSurface, setEditorSurface] = useState<HTMLDivElement | null>(null);
  const linkUrlInputId = 'pd-opportunity-notes-link-url-' + useId().replace(/:/g, '');
  const saveTimer = useRef<number | undefined>(undefined);
  const loadedNoteId = useRef('');
  const baseVersion = useRef(0);
  const noteId = useRef<string | undefined>(undefined);
  const draftMarkdown = useRef('');
  const savedMarkdown = useRef('');
  const saveRequested = useRef(false);
  const saveLoop = useRef<Promise<boolean> | null>(null);
  const flushSave = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
  const mounted = useRef(true);

  useEffect(() => {
    const root = editorSurface;
    if (!root) return;

    const connectLinkUrlLabel = () => {
      for (const label of root.querySelectorAll<HTMLLabelElement>('label[for="link-url"]')) {
        const input = label.parentElement?.querySelector<HTMLInputElement>('input[name="url"]');
        if (!input || label.htmlFor === input.id) continue;
        const inputId = input.id || linkUrlInputId;
        input.id = inputId;
        label.htmlFor = inputId;
      }
    };

    connectLinkUrlLabel();
    const observer = new MutationObserver(connectLinkUrlLabel);
    observer.observe(root, {childList: true, subtree: true});
    return () => observer.disconnect();
  }, [editorSurface, linkUrlInputId]);

  const notes = useQuery({
    queryKey: ['notes', 'opportunity', opportunityId],
    queryFn: () =>
      request<PageResult<NoteRecord>>(
        '/opportunities/' + encodeURIComponent(opportunityId) + '/notes',
      ),
  });
  const documents = useQuery({
    queryKey: ['documents'],
    queryFn: () => request<PageResult<DocumentArtifact>>('/documents?limit=200'),
  });

  const visibleNotes = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    const items = notes.data?.items ?? [];
    if (!needle) return items;
    return items.filter((note) =>
      [note.title, note.markdown].some((value) => value.toLocaleLowerCase().includes(needle)),
    );
  }, [notes.data, search]);
  const currentNote = notes.data?.items.find((note) => note.id === activeNoteId) ?? notes.data?.items[0];

  const revisionHistory = useInfiniteQuery({
    queryKey: ['note-revisions', currentNote?.id ?? 'none'],
    queryFn: ({pageParam}) => {
      const cursor = typeof pageParam === 'number' ? '&beforeSequence=' + pageParam : '';
      return request<NoteRevisionPage>(
        '/notes/' + encodeURIComponent(currentNote!.id) + '/revisions?limit=50' + cursor,
      );
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextBeforeSequence,
    enabled: Boolean(currentNote),
  });

  useEffect(() => {
    if (!currentNote || loadedNoteId.current === currentNote.id) return;
    loadedNoteId.current = currentNote.id;
    noteId.current = currentNote.id;
    baseVersion.current = currentNote.version;
    draftMarkdown.current = currentNote.markdown;
    savedMarkdown.current = currentNote.markdown;
    setActiveNoteId(currentNote.id);
    setNoteValue(currentNote.markdown);
    setEditorGeneration((generation) => generation + 1);
    setLastSavedAt(currentNote.updatedAt);
    setSaveState({phase: 'saved'});
    setSelectedRevisionId('');
  }, [currentNote]);

  useEffect(() => {
    mounted.current = true;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (draftMarkdown.current === savedMarkdown.current && !saveLoop.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      if (draftMarkdown.current !== savedMarkdown.current) void flushSave.current();
      mounted.current = false;
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, []);

  const restore = useMutation({
    mutationFn: (revisionId: string) => {
      if (!currentNote) throw new Error('Choose a note revision first.');
      return request<NoteRecord>(
        '/notes/' + encodeURIComponent(currentNote.id) + '/revisions/' + encodeURIComponent(revisionId) + '/restore',
        {method: 'POST', body: JSON.stringify({expectedVersion: baseVersion.current})},
      );
    },
    onSuccess: async (restored) => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      loadedNoteId.current = restored.id;
      noteId.current = restored.id;
      draftMarkdown.current = restored.markdown;
      savedMarkdown.current = restored.markdown;
      setNoteValue(restored.markdown);
      setEditorGeneration((generation) => generation + 1);
      baseVersion.current = restored.version;
      setLastSavedAt(restored.updatedAt);
      setSaveState({phase: 'saved'});
      setSelectedRevisionId(restored.latestRevisionId ?? '');
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['notes', 'opportunity', opportunityId]}),
        queryClient.invalidateQueries({queryKey: ['note-revisions', restored.id]}),
      ]);
    },
  });

  const updateAttachments = useMutation({
    mutationFn: (documentId: string) => {
      if (!currentNote) throw new Error('Save the note before linking an attachment.');
      const currentRefs = currentNote.sourceRefs ?? [];
      const sourceRefs = currentRefs.includes(documentId)
        ? currentRefs.filter((id) => id !== documentId)
        : [...currentRefs, documentId];
      return request<NoteRecord>('/notes/' + encodeURIComponent(currentNote.id), {
        method: 'PATCH',
        body: JSON.stringify({sourceRefs}),
      });
    },
    onSuccess: (saved) => {
      baseVersion.current = saved.version;
      return queryClient.invalidateQueries({queryKey: ['notes', 'opportunity', opportunityId]});
    },
  });

  const saveLatest = (): Promise<boolean> => {
    saveRequested.current = true;
    if (saveLoop.current) {
      if (mounted.current && draftMarkdown.current !== savedMarkdown.current) {
        setSaveState({phase: 'queued'});
      }
      return saveLoop.current;
    }

    const running = (async () => {
      let completed = true;
      while (saveRequested.current) {
        saveRequested.current = false;
        const markdown = draftMarkdown.current;
        const targetNoteId = noteId.current;
        if (markdown === savedMarkdown.current || (!targetNoteId && !markdown.trim())) continue;
        if (mounted.current) setSaveState({phase: 'saving'});

        try {
          const saved = targetNoteId
            ? await request<NoteRecord>('/notes/' + encodeURIComponent(targetNoteId) + '/revisions', {
                method: 'POST',
                body: JSON.stringify({
                  markdown,
                  reason: 'autosave',
                  expectedVersion: baseVersion.current,
                }),
              })
            : await request<NoteRecord>('/notes', {
                method: 'POST',
                body: JSON.stringify({
                  title: 'Opportunity notes',
                  markdown,
                  linkedEntityType: 'opportunity',
                  linkedEntityId: opportunityId,
                  source: 'user',
                  sourceRefs: [],
                }),
              });

          noteId.current = saved.id;
          loadedNoteId.current = saved.id;
          baseVersion.current = saved.version;
          savedMarkdown.current = markdown;
          if (mounted.current) {
            if (!targetNoteId) setActiveNoteId(saved.id);
            setLastSavedAt(saved.updatedAt);
          }
          void Promise.all([
            queryClient.invalidateQueries({queryKey: ['notes', 'opportunity', opportunityId]}),
            queryClient.invalidateQueries({queryKey: ['note-revisions', saved.id]}),
          ]).catch(() => undefined);

          if (draftMarkdown.current !== markdown) {
            saveRequested.current = true;
            if (mounted.current) setSaveState({phase: 'queued'});
          } else if (mounted.current) {
            setSaveState({phase: 'saved'});
          }
        } catch (caught) {
          completed = false;
          saveRequested.current = false;
          const error = caught instanceof Error ? caught : new Error('Autosave failed.');
          if (mounted.current) {
            setSaveState({
              phase: 'error',
              error,
              conflict: error instanceof ApiError && error.code === 'note_revision_conflict',
            });
          }
          break;
        }
      }
      return completed && draftMarkdown.current === savedMarkdown.current;
    })();

    saveLoop.current = running;
    void running.finally(() => {
      saveLoop.current = null;
      if (saveRequested.current && draftMarkdown.current !== savedMarkdown.current) {
        void saveLatest();
      }
    });
    return running;
  };
  flushSave.current = saveLatest;

  const autosave = (markdown: string) => {
    draftMarkdown.current = markdown;
    setNoteValue(markdown);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    if (markdown === savedMarkdown.current) {
      if (!saveLoop.current) setSaveState({phase: 'saved'});
      return;
    }
    setSaveState({phase: saveLoop.current ? 'queued' : 'dirty'});
    saveTimer.current = window.setTimeout(() => void saveLatest(), 900);
  };

  const selectNote = async (id: string) => {
    if (id === noteId.current) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    const safeToLeave = await saveLatest();
    if (!safeToLeave) return;
    loadedNoteId.current = '';
    noteId.current = undefined;
    setActiveNoteId(id);
  };

  const retrySave = async () => {
    if (saveState.conflict && noteId.current) {
      try {
        setSaveState({phase: 'saving'});
        const latest = await request<NoteRecord>('/notes/' + encodeURIComponent(noteId.current));
        baseVersion.current = latest.version;
        savedMarkdown.current = latest.markdown;
        setLastSavedAt(latest.updatedAt);
        if (draftMarkdown.current === latest.markdown) {
          setSaveState({phase: 'saved'});
          return;
        }
      } catch (caught) {
        const error = caught instanceof Error ? caught : new Error('The latest note could not be loaded.');
        setSaveState({phase: 'error', error, conflict: true});
        return;
      }
    }
    await saveLatest();
  };

  const revisions = useMemo(
    () => revisionHistory.data?.pages.flatMap((revisionPage) => revisionPage.items) ?? [],
    [revisionHistory.data],
  );
  const selectedRevision = revisions.find((revision) => revision.id === selectedRevisionId);
  const changes = selectedRevision ? lineChanges(selectedRevision.markdown, savedMarkdown.current) : [];

  useEffect(() => {
    if (!selectedRevisionId && revisions[0]) setSelectedRevisionId(revisions[0].id);
  }, [revisions, selectedRevisionId]);

  const saveStatusLabel = saveState.phase === 'dirty'
    ? 'Unsaved changes'
    : saveState.phase === 'saving'
      ? 'Saving…'
      : saveState.phase === 'queued'
        ? 'Latest edits queued'
        : saveState.phase === 'error'
          ? 'Save needs attention'
          : lastSavedAt
            ? 'Saved ' + formatRelativeDate(lastSavedAt)
            : 'New note';

  return (
    <div className="pd-notes-workspace">
      <div className="pd-notes-main">
        <label className="pd-search-control pd-note-search">
          <Search size={17} aria-hidden="true" />
          <span className="pd-visually-hidden">Search opportunity notes</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search note titles and full text"
          />
        </label>

        {visibleNotes.length > 1 || search ? (
          <div className="pd-note-results" aria-label="Matching notes">
            {visibleNotes.map((note) => (
              <button
                key={note.id}
                type="button"
                className={note.id === currentNote?.id ? 'is-active' : ''}
                onClick={() => void selectNote(note.id)}
              >
                <strong>{note.title || 'Untitled note'}</strong>
                <span>{note.markdown.replace(/\s+/g, ' ').slice(0, 110)}</span>
              </button>
            ))}
            {!visibleNotes.length ? <p>No notes match this full-text filter.</p> : null}
          </div>
        ) : null}

        <Surface ref={setEditorSurface} className="pd-note-editor">
          <div className="pd-note-heading">
            <div>
              <span className="pd-eyebrow">Revision-safe autosave</span>
              <h3>{currentNote?.title || 'Opportunity notes'}</h3>
            </div>
            <span className={'pd-note-save-status is-' + saveState.phase} aria-live="polite">{saveStatusLabel}</span>
          </div>
          <MDXEditor
            key={(currentNote?.id ?? 'new-note') + ':' + editorGeneration}
            markdown={noteValue}
            onChange={autosave}
            overlayContainer={editorSurface}
            contentEditableClassName="pd-markdown-editor"
            placeholder="Interview notes, questions, recruiter context, decisions…"
            plugins={[
              headingsPlugin(),
              listsPlugin(),
              linkPlugin(),
              linkDialogPlugin(),
              toolbarPlugin({
                toolbarContents: () => (
                  <>
                    <UndoRedo />
                    <BoldItalicUnderlineToggles />
                    <CreateLink />
                  </>
                ),
              }),
            ]}
          />
          <div className="pd-note-footer">
            <span>{currentNote?.revisionCount ?? 0} revisions retained</span>
            {currentNote?.linkedEntityType && currentNote.linkedEntityType !== 'general' ? (
              <span>Linked to {humanizeStage(currentNote.linkedEntityType)}</span>
            ) : null}
            <span>{currentNote?.source === 'ai' ? 'AI-generated · inspect source references' : 'Candidate-authored'}</span>
            {saveState.phase === 'error' ? (
              <div className="pd-note-save-error" role="alert">
                <strong>{saveState.error?.message ?? 'Autosave failed.'}</strong>
                <span>
                  {saveState.conflict
                    ? 'A newer revision exists. Your draft remains here and can be appended after that version.'
                    : 'Your draft remains in this editor. Retry when the connection is available.'}
                </span>
                <SecondaryButton size="compact-sm" onClick={() => void retrySave()}>
                  {saveState.conflict ? 'Save draft after latest' : 'Retry save'}
                </SecondaryButton>
              </div>
            ) : null}
          </div>
        </Surface>
      </div>

      <aside className="pd-note-rail">
        <Surface>
          <div className="pd-rail-heading">
            <History size={18} aria-hidden="true" />
            <div><span className="pd-eyebrow">History</span><h3>Revisions</h3></div>
          </div>
          {revisions.length ? (
            <div className="pd-revision-list">
              {revisions.map((revision) => (
                <button
                  key={revision.id}
                  type="button"
                  className={revision.id === selectedRevisionId ? 'is-active' : ''}
                  onClick={() => setSelectedRevisionId(revision.id)}
                >
                  <Clock3 size={14} aria-hidden="true" />
                  <span>
                    <strong>{revision.reason.startsWith('restore:') ? 'Restored version' : revision.reason}</strong>
                    <small>{formatDateTime(revision.createdAt)}</small>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="pd-empty-copy">Revision history appears after the first autosave.</p>
          )}
          {revisionHistory.hasNextPage ? (
            <SecondaryButton
              loading={revisionHistory.isFetchingNextPage}
              onClick={() => void revisionHistory.fetchNextPage()}
            >
              Load older revisions
            </SecondaryButton>
          ) : null}
          {selectedRevision ? (
            <>
              <div className="pd-revision-diff" aria-label="Revision changes">
                <strong>{changes.length} changed line{changes.length === 1 ? '' : 's'}</strong>
                {changes.length ? changes.map((change) => (
                  <div key={change.number}>
                    <span>Line {change.number}</span>
                    <del>{change.before ?? 'No earlier line'}</del>
                    <ins>{change.after ?? 'Line removed'}</ins>
                  </div>
                )) : <p>This revision matches the current saved note.</p>}
              </div>
              <SecondaryButton
                leftSection={<RotateCcw size={15} aria-hidden="true" />}
                loading={restore.isPending}
                disabled={saveState.phase !== 'saved'}
                onClick={() => restore.mutate(selectedRevision.id)}
              >
                Restore as a new revision
              </SecondaryButton>
            </>
          ) : null}
          {restore.isError ? (
            <p className="pd-form-error" role="alert">
              {restore.error instanceof Error ? restore.error.message : 'The revision could not be restored.'}
            </p>
          ) : null}
        </Surface>

        <Surface>
          <div className="pd-rail-heading">
            <Paperclip size={18} aria-hidden="true" />
            <div><span className="pd-eyebrow">Links</span><h3>Attachments</h3></div>
          </div>
          <p>Link existing candidate-owned documents without copying their contents into the note.</p>
          <div className="pd-note-attachments">
            {documents.data?.items.map((document) => (
              <label key={document.id}>
                <input
                  type="checkbox"
                  checked={Boolean(currentNote?.sourceRefs?.includes(document.id))}
                  disabled={!currentNote || updateAttachments.isPending || saveState.phase !== 'saved'}
                  onChange={() => updateAttachments.mutate(document.id)}
                />
                <FileText size={15} aria-hidden="true" />
                <span>{document.name}</span>
              </label>
            ))}
            {documents.data && documents.data.items.length === 0 ? <p className="pd-empty-copy">No documents are available yet.</p> : null}
            {!currentNote ? <p className="pd-empty-copy">Save the note before linking documents.</p> : null}
          </div>
          {updateAttachments.isError ? (
            <p className="pd-form-error" role="alert">
              {updateAttachments.error instanceof Error ? updateAttachments.error.message : 'The attachment link could not be saved.'}
            </p>
          ) : null}
        </Surface>
      </aside>
    </div>
  );
};
